/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { FC, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { TypographyStyles } from 'src/styles';
import {
  getQuestionFromAnswerScheme,
  groupOptionsByInnerScheme,
  isConditionalOption,
} from 'src/helpers/question-helper';
import Style from './surveyTableRow.module.css';
import { ISurveyOption, ISurveyQuestion, QuestionType } from 'src/services/api/api-client';
import { Tooltip } from 'src/components/uikit/tooltip/tooltip.component';
import { ReactComponent as CommentIcon } from '../../assets/img/survey/comment.svg';
import { ReactComponent as MultiselectIcon } from '../../assets/img/survey/b_multiselect_24.svg';
import { ReactComponent as CheckIcon } from '../../assets/img/survey/check_16.svg';
import { ReactComponent as InfoIcon } from '../../assets/img/common/f_info_circle_16.svg';
import { isNullOrEmpty } from 'src/helpers/string-helper';
import { AppSwiper } from 'src/components/swiper/appSwiper.component';
import { AnswersTableRowProps } from './surveyAnswers';
import { slideWidthPx } from './constants';
import { SurveyAnswersColumnsContext } from './surveyAnswersLayout';
import { SwiperSlide } from 'swiper/react';
import { ExpandedRow } from './expandedRow.component';
import { useScopedTranslation } from '../../application/localisation/useScopedTranslation';
import { DateFormats, formatDateRange, localFormat, toDateOrNull } from 'src/helpers/date-helpers';

export const AnswersTableRow: FC<AnswersTableRowProps> = ({
  viewMode,
  parentQuestion,
  question,
  answers,
  swipeIndex,
  index,
}) => {
  const { t } = useScopedTranslation('Dashboard.PatientAccordion.SurveySection');
  const visibleColumns = useContext(SurveyAnswersColumnsContext);
  const isContent = question.questionType === QuestionType.Content;

  useLayoutEffect(() => {
    const elements = document.getElementsByClassName('question-row');
    for (let i = 0; i < elements.length - 1; i++) {
      elements[i]?.classList.add(Style.borderBottom);
    }
  }, []);

  const answerComponentList = useMemo(() => {
    return answers?.map((answerScheme, i) => {
      if (isContent) return null;

      // In this case, the user missed the survey
      if (!answerScheme) {
        return (
          <SwiperSlide
            key={i}
            className={clsx(Style.answer, Style.empty, { [Style.singleAnswer]: viewMode === 'single' })}
          />
        );
      }

      if (!question.id) {
        throw new Error('Question ID is not exists');
      }

      const questionFromAnswerScheme = getQuestionFromAnswerScheme(answerScheme, parentQuestion, question);

      // This question was not shown to the user
      if (!questionFromAnswerScheme) {
        return (
          <SwiperSlide
            key={i}
            className={clsx(Style.answer, Style.empty, { [Style.singleAnswer]: viewMode === 'single' })}
          >
            <div className={Style.skippedQuestion}>{t('LastAvailableStats.N/a')}</div>
          </SwiperSlide>
        );
      }

      // This question was skipped by checked 'No answer' option
      const answerOption = questionFromAnswerScheme.options?.find((option) => !isNullOrEmpty(option.answerValue));
      if (!answerOption || isNullOrEmpty(answerOption.answerValue)) {
        return (
          <SwiperSlide
            key={i}
            className={clsx(Style.answer, Style.empty, { [Style.singleAnswer]: viewMode === 'single' })}
          >
            <div className={Style.skippedQuestion}>{t('LastAvailableStats.Skipped')}</div>
          </SwiperSlide>
        );
      }

      return (
        <SwiperSlide key={i} className={clsx(Style.answer, { [Style.singleAnswer]: viewMode === 'single' })}>
          <AnswerValue
            question={questionFromAnswerScheme}
            answer={answerOption}
            isSingleAnswer={viewMode === 'single'}
          />
        </SwiperSlide>
      );
    });
  }, [answers, question, parentQuestion, viewMode, t, isContent]);

  return (
    <div className={clsx(TypographyStyles.plainText14, Style.container)}>
      <div className={clsx('question-row', Style.row)}>
        <div className={Style.index}>{index}</div>
        <div className={Style.questionTextContainer}>
          {isContent ? (
            <ContentQuestion question={question} />
          ) : (
            <div className={Style.questionText}>{question.questionText}</div>
          )}
        </div>
        <AppSwiper
          slideWidth={viewMode === 'single' ? 200 : slideWidthPx}
          slidesPerView={viewMode === 'single' ? 1 : visibleColumns}
          containerStyles={Style.answersContainer}
          swipeIndex={swipeIndex}
        >
          {answerComponentList}
        </AppSwiper>
        <div className={Style.lastColumn} />
      </div>

      {Object.entries(groupOptionsByInnerScheme(question.options.filter(isConditionalOption))).map(([key, options]) => {
        return (
          <ExpandedRow
            key={key}
            question={question}
            groupedOptions={options}
            viewMode={viewMode}
            swipeIndex={swipeIndex}
            answers={answers}
          />
        );
      })}
    </div>
  );
};

const AnswerValue: FC<{ question: ISurveyQuestion; answer: ISurveyOption; isSingleAnswer?: boolean }> = ({
  question,
  answer,
  isSingleAnswer,
}) => {
  const [isEllipsis, setIsEllipsis] = useState(false);
  const textRef = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    const element = textRef.current;
    if (element) {
      setIsEllipsis(element.scrollHeight > element.clientHeight);
    }
  }, [answer?.answerValue]);

  switch (question.questionType) {
    case QuestionType.Scale:
    case QuestionType.SingleChose:
      return question.isYesNoQuestion ? (
        <>{answer?.optionText}</>
      ) : (
        <Tooltip
          text={answer?.optionText ?? ''}
          headerText={''}
          textStyles={TypographyStyles.plainText14}
          hostStyles={Style.tooltipHost}
          disabled={!answer?.optionText}
        >
          {answer?.answerValue}
        </Tooltip>
      );
    case QuestionType.ConditionalQuestion:
      return <>{answer?.optionText}</>;

    case QuestionType.DateInput:
      const date = toDateOrNull(answer?.answerValue);
      return <div className={Style.dateField}>{date ? localFormat(date, DateFormats.longDate) : ''}</div>;

    case QuestionType.DateRangeInput:
      const startDate = toDateOrNull(question.options[0]?.answerValue);
      const endDate = toDateOrNull(question.options[1]?.answerValue);
      return <div className={Style.dateField}>{formatDateRange(startDate, endDate)}</div>;

    case QuestionType.MultipleChose:
      return <MultipleChoiceAnswer options={question.options} />;

    case QuestionType.Content:
      return null;

    case QuestionType.FreeInput:
      if (isNullOrEmpty(answer?.answerValue)) {
        return <></>;
      }

      if (isSingleAnswer) {
        return (
          <Tooltip
            text={answer?.answerValue ?? ''}
            disabled={!isEllipsis}
            calloutContainerStyles={Style.singleAnswerTooltip}
          >
            <p ref={textRef} className={clsx(TypographyStyles.paragraph12, Style.singleAnswerText)}>
              {answer?.answerValue}
            </p>
          </Tooltip>
        );
      }

      return (
        <Tooltip text={answer?.answerValue ?? ''} textStyles={TypographyStyles.paragraph14}>
          <CommentIcon />
        </Tooltip>
      );

    default:
      return <>{answer?.optionText}</>;
  }
};

const MultipleChoiceAnswer: FC<{ options: ISurveyOption[] }> = ({ options }) => {
  const selectedOptions = options.filter((option) => !isNullOrEmpty(option.answerValue));

  return (
    <Tooltip
      disabled={selectedOptions.length === 0}
      calloutContainerStyles={Style.multipleChoiceTooltip}
      tooltipContent={
        <ul className={clsx(TypographyStyles.plainText12, Style.multipleChoiceList)}>
          {selectedOptions.map((option, index) => (
            <li key={index} className={Style.multipleChoiceOption}>
              <CheckIcon />
              <span>{option.optionText || option.answerValue}</span>
            </li>
          ))}
        </ul>
      }
    >
      <MultiselectIcon />
    </Tooltip>
  );
};

const ContentQuestion: FC<{ question: ISurveyQuestion }> = ({ question }) => {
  const { t } = useScopedTranslation('Dashboard.PatientAccordion.SurveySection');
  const firstContentBlock = question.contentBlock?.[0];

  if (!firstContentBlock) {
    return null;
  }

  return (
    <div className={Style.contentQuestion}>
      <Tooltip text={t('SurveyFilling.InfoBlock')}>
        <InfoIcon className={Style.contentInfoIcon} />
      </Tooltip>
      <div className={Style.contentQuestionText}>
        {firstContentBlock.titleText && <p className={TypographyStyles.heading2}>{firstContentBlock.titleText}</p>}
        {firstContentBlock.descriptionText && (
          <p className={TypographyStyles.paragraph14}>{firstContentBlock.descriptionText}</p>
        )}
      </div>
    </div>
  );
};
